import React, { useState, useEffect, useRef, useCallback } from "react";
import "../Style/Webprocess.css";
import trapLeft from "../assets/left-corner.png";
import trapRight from "../assets/right-corner.png";

import icon1 from "../assets/projects-icon.png";
import icon2 from "../assets/clients-icon.png";
import icon3 from "../assets/experience-icon.png";
import icon4 from "../assets/team-icon.png";

const stats = [
  { icon: icon1, value: 250, suffix: "+", label: "Projects" },
  { icon: icon2, value: 50, suffix: "+", label: "Satisfied Clients" },
  { icon: icon3, value: 6, suffix: "+", label: "Industry Experience" },
  { icon: icon4, value: 20, suffix: "+", label: "Development People's" },
];

const slides = [
  {
    slideClass: "wp-slide-1",
    step: "Step-1",
    heading: "Domain & Website Setup",
    desc: "We begin by establishing a strong digital foundation through domain registration, secure hosting configuration, and server setup. This ensures your website is reliable, secure, and optimized for performance before development begins.",
    benefits: [
      "Professional domain registration",
      "Secure hosting setup",
      "Reliable website infrastructure",
    ],
    approach: [
      "Brand-focused domain selection",
      "DNS and server configuration",
      "SSL certificate installation",
    ],
  },
  {
    slideClass: "wp-slide-2",
    step: "Step-2",
    heading: "Strategy & Planning",
    desc: "Every successful website begins with a clear strategy. We analyze your business goals, target audience, competitors, and market opportunities to create a customized website development plan that aligns with your brand and supports long-term digital growth.",
    benefits: [
      "Clear website development roadmap",
      "Audience-focused planning",
      "Strong brand positioning",
    ],
    approach: [
      "Business and competitor analysis",
      "User persona and audience research",
      "Goal-driven website architecture",
    ],
  },
  {
    slideClass: "wp-slide-3",
    step: "Step-3",
    heading: "UX & UI Design",
    desc: "Our UI/UX design process focuses on creating visually engaging, user-friendly, and conversion-driven websites. We design intuitive interfaces, responsive layouts, and seamless user journeys that improve customer engagement, strengthen brand identity, and enhance overall website performance.",
    benefits: [
      "Modern and attractive website design",
      "Seamless user experience",
      "Improved customer engagement",
    ],
    approach: [
      "User-centered design methodology",
      "Wireframing and interactive prototyping",
      "Responsive UI design for all devices",
    ],
  },
  {
    slideClass: "wp-slide-4",
    step: "Step-4",
    heading: "Development & Testing",
    desc: "Our expert web developers transform approved designs into fast, secure, and responsive websites using modern technologies and industry best practices. Every feature is thoroughly tested for performance, functionality, browser compatibility, mobile responsiveness, and security to ensure your website delivers a flawless user experience across all devices.",
    benefits: [
      "Fast-loading and responsive website",
      "Secure, bug-free functionality",
      "Optimized user experience across all devices",
    ],
    approach: [
      "Clean, scalable, and maintainable code",
      "Cross-browser and mobile compatibility testing",
      "Website speed, security, and performance optimization",
    ],
  },
  {
    slideClass: "wp-slide-5",
    step: "Step-5",
    heading: "Launch & Hosting",
    desc: "After rigorous quality assurance, we deploy your website to a secure hosting environment and configure everything for a successful launch. From domain connection and SSL installation to ongoing monitoring and performance optimization, we ensure your website is reliable, secure, and ready to support your business growth.",
    benefits: [
      "Smooth and secure website launch",
      "High-performance hosting environment",
      "Reliable online presence with maximum uptime",
    ],
    approach: [
      "Safe deployment and server configuration",
      "SSL, DNS, and hosting optimization",
      "Continuous monitoring, backups, and maintenance support",
    ],
  },
  {
    slideClass: "wp-slide-6",
    step: "Step-6",
    heading: "Maintenance & Support",
    desc: "Your website needs continuous care to stay secure, fast, and up to date. Our website maintenance and support services include regular updates, security monitoring, bug fixes, performance optimization, backups, and technical assistance to ensure your website delivers a reliable experience and supports your business growth.",
    benefits: [
      "Regular updates and fixes",
      "Enhanced website security",
      "Consistent performance and uptime",
    ],
    approach: [
      "Proactive website maintenance",
      "Continuous security monitoring",
      "Long-term technical support and optimization",
    ],
  },
  {
    slideClass: "wp-slide-7",
    isStats: true,
  },
];

const TOTAL = slides.length;

/* =========================
   COUNT UP COMPONENT
========================= */
const CountUp = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;

    const duration = 1800;
    const increment = end / (duration / 16);

    const counter = setInterval(() => {
      start += increment;

      if (start >= end) {
        setCount(end);
        clearInterval(counter);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(counter);
  }, [end]);

  return (
    <>
      {count}
      {suffix}
    </>
  );
};

const Webprocess = () => {
  const [current, setCurrent] = useState(0);
  const [animDir, setAnimDir] = useState(null);
  const [exiting, setExiting] = useState(false);

  const isAnimating = useRef(false);
  const exitingIndex = useRef(null);

  const goTo = useCallback(
    (nextIndex, direction) => {
      if (isAnimating.current) return;
      if (nextIndex === current) return;

      isAnimating.current = true;
      exitingIndex.current = current;
      setAnimDir(direction);
      setExiting(true);

      setTimeout(() => {
        setExiting(false);
        setCurrent(nextIndex);
        exitingIndex.current = null;

        setTimeout(() => {
          isAnimating.current = false;
          setAnimDir(null);
        }, 700);
      }, 420);
    },
    [current],
  );

  const goNext = useCallback(() => {
    if (current < TOTAL - 1) goTo(current + 1, "next");
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    if (current > 0) goTo(current - 1, "prev");
  }, [current, goTo]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") goPrev();
    };

    window.addEventListener("keydown", handleKey);

    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  const getSlideClass = (i) => {
    const classes = ["wp-section", slides[i].slideClass];

    if (i === current && !exiting) {
      classes.push(animDir ? `wp-enter-${animDir}` : "wp-visible");
    }

    if (exiting && i === exitingIndex.current) {
      classes.push(`wp-exit-${animDir}`);
    }

    return classes.join(" ");
  };

  return (
    <div className="wp-outer">
      {/* Slides */}
      <div className="wp-stage">
        {slides.map((slide, i) => {
          const isVisible = i === current;
          const isExit = exiting && i === exitingIndex.current;

          if (!isVisible && !isExit) return null;

          return slide.isStats ? (
            /* =========================
               LAST STATS SLIDE
            ========================= */
            <section key={i} className={getSlideClass(i)}>
              <img src={trapLeft} alt="" className="wp-corner-left" />
              <img src={trapRight} alt="" className="wp-corner-right" />

              <div className="wp-stats-container">
                <h2 className="wp-stats-heading">
                  Interesting Facts And Figures Of Our Success
                </h2>

                <div className="wp-stats-grid">
                  {stats.map((stat, j) => (
                    <div key={j} className="wp-stat-item">
                      <div className="wp-stat-circle">
                        <img
                          src={stat.icon}
                          alt={stat.label}
                          className="wp-stat-icon"
                        />
                      </div>

                      {/* 🔥 COUNTING NUMBER */}
                      <div className="wp-stat-value">
                        {current === TOTAL - 1 && (
                          <CountUp end={stat.value} suffix={stat.suffix} />
                        )}
                      </div>

                      <div className="wp-stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="wp-stats-cta">
                  <p className="wp-cta-text">
                    Start Your Digital Transformation With Us
                  </p>

                  <button className="wp-cta-btn">Connect With Us</button>
                </div>
              </div>
            </section>
          ) : (
            /* =========================
               NORMAL SLIDES
            ========================= */
            <section key={i} className={getSlideClass(i)}>
              <img src={trapLeft} alt="" className="wp-corner-left" />
              <img src={trapRight} alt="" className="wp-corner-right" />

              <div className="wp-container">
                <div className="wp-header">
                  <div className="wp-title">
                    <h1>
                      Our Website Development Process: <br />
                      From Planning to Launch
                    </h1>
                  </div>

                  <div className="wp-subtext">
                    <p>
                      Our proven website development process ensures every
                      project is delivered with precision, transparency, and
                      quality. From domain setup to deployment, we follow a
                      structured workflow that creates secure, scalable, and
                      SEO-optimized websites ready for business growth.
                    </p>
                  </div>
                </div>

                <div className="wp-divider"></div>

                <div className="wp-content">
                  <div className="wp-left">
                    <span className="wp-step-label">{slide.step}</span>

                    <h2 className="wp-step-heading">{slide.heading}</h2>

                    <p className="wp-step-desc">{slide.desc}</p>
                  </div>

                  <div className="wp-right">
                    <div className="wp-block">
                      <h3 className="wp-block-title">Client Benefits</h3>

                      <ul className="wp-block-list">
                        {slide.benefits.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="wp-small-divider"></div>

                    <div className="wp-block">
                      <h3 className="wp-block-title">Our Approach</h3>

                      <ul className="wp-block-list">
                        {slide.approach.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* PREV BUTTON */}
      <button
        className="wp-nav-prev"
        onClick={goPrev}
        disabled={current === 0}
        aria-label="Previous slide"
      >
        <svg viewBox="0 0 24 24">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {/* NEXT BUTTON */}
      <button
        className="wp-nav-next"
        onClick={goNext}
        disabled={current === TOTAL - 1}
        aria-label="Next slide"
      >
        <svg viewBox="0 0 24 24">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>

      {/* DOTS */}
      <div className="wp-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`wp-dot${i === current ? " wp-dot-active" : ""}`}
            onClick={() => goTo(i, i > current ? "next" : "prev")}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* COUNTER */}
      <div className="wp-slide-counter">
        {String(current + 1).padStart(2, "0")} /{" "}
        {String(TOTAL).padStart(2, "0")}
      </div>
    </div>
  );
};

export default Webprocess;
